import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Status } from '../api/client'
import { requireAccessToken } from '../auth/msal'
import { invalidateFleet } from '../api/invalidate'
import { SearchSelect } from '../components/SearchSelect'
import {
  siteOptions,
  statusOptions,
  vehicleTypeOptions,
} from '../components/referenceOptions'
// Field, note and button styles are shared with the compliance editor; imported
// so they load on this route in dev, where CSS follows module imports.
import '../components/ComplianceEditor.css'
import './AddVehicle.css'

interface ObligationDraft {
  status: number
  expiryDate: string

  /**
   * The scan for this obligation, held until the vehicle exists.
   *
   * It cannot be uploaded during the add: a document is attached to a
   * VehicleComplianceId, and there is no vehicle to attach it to until the record
   * is created. So the file is kept here and uploaded immediately afterwards.
   */
  file?: File
}

/** What happened to the scans after the vehicle itself was created. */
interface AttachOutcome {
  vehicleId: number
  attached: number
  failures: { obligation: string; message: string }[]
}

/**
 * Canonicalises a registration number the same way the server does: uppercase,
 * strip every separator.
 *
 * Duplicated from the C# RegistrationNumber deliberately, and only to show the
 * user what will be stored as they type. The server remains the authority — this
 * is feedback, never validation.
 */
function canonicalise(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function AddVehicle() {
  const navigate = useNavigate()
  const [registration, setRegistration] = useState('')
  const [vehicleTypeId, setVehicleTypeId] = useState<number | ''>('')
  const [siteId, setSiteId] = useState<number | ''>('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [chassisNumber, setChassisNumber] = useState('')
  const [registrationYear, setRegistrationYear] = useState('')
  const [assetCode, setAssetCode] = useState('')
  const [obligations, setObligations] = useState<Record<number, ObligationDraft>>({})

  const queryClient = useQueryClient()

  const onClose = () => navigate('/register')

  const reference = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.getReference(),
    staleTime: 30 * 60 * 1000,
  })

  const canonical = useMemo(() => canonicalise(registration), [registration])

  // Checked as the user types, not on submit. Discovering a duplicate after
  // filling in seven obligations is a waste of their time — and the useful
  // answer is "here is the existing record, edit that", not "no".
  const existing = useQuery({
    queryKey: ['vehicle-lookup', canonical],
    queryFn: () => api.findByRegistration(canonical),
    enabled: canonical.length >= 6,
    staleTime: 10_000,
  })

  const registry = useQuery({
    queryKey: ['registry-status'],
    queryFn: () => api.getRegistryStatus(),
    staleTime: 10 * 60 * 1000,
  })

  /*
      A registration that is on the register and a registration that was REMOVED
      from it are different answers, and only the first is a dead end.

      Soft delete keeps the row, and UQ_Vehicle_RegCanonical is unique over every
      row deleted or not — so treating both the same made a removed registration
      permanently unusable: adding it back was refused as a duplicate, while the
      vehicle it pointed at stayed hidden. Reported against MH12NE4400.
  */
  const blocked = Boolean(existing.data) && !existing.data?.isDeleted
  const removed = Boolean(existing.data?.isDeleted)

  const selectedType = reference.data?.vehicleTypes?.find(
    (t) => t.vehicleTypeId === vehicleTypeId,
  )

  const [attachOutcome, setAttachOutcome] = useState<AttachOutcome | null>(null)

  /** What the RTO fetch produced, shown as a confirmation once it has saved. */
  const [rtoSaved, setRtoSaved] = useState<{
    registration: string
    applied: number
    identity: number
    reused: boolean
    restored: boolean
    notes: string[]
  } | null>(null)

  const [rtoError, setRtoError] = useState<string | null>(null)

  /*
      Fetch from the RTO and save, in one press.

      It CREATES the vehicle first and then syncs it, rather than previewing into
      the form and waiting for a separate save. Two reasons: the sync endpoint is
      per vehicle and needs something to attach a RegistryFetch row to, so a
      preview would either need a second code path or would spend money against
      nothing; and a fetch that filled the form but was never saved would have
      cost ₹3.50 for data that vanished on a page refresh.

      The type still has to be chosen by hand. VAHAN returns a class like LMV,
      which maps onto four of RSIIL's twenty business categories with nothing in
      the response to choose between - and that choice decides which obligations
      apply. Seed_VehicleType.sql is the authority, not the registry.
  */
  const syncNew = useMutation({
    mutationFn: async () => {
      /*
          Get the token FIRST, before any work.

          This flow makes two calls, and a token that expires while the form is
          being filled has to be refreshed. If that refresh needs a popup, it can
          only open while the click is still fresh — after the first `await` the
          browser has withdrawn user activation and blocks it, `getAccessToken`
          returns null, and the request goes out with no Authorization header for
          the API to correctly refuse. That is what "Request failed (401)" was.
      */
      await requireAccessToken()

      const created = await api.createVehicle({
        registration,
        vehicleTypeId: vehicleTypeId as number,
        siteId: siteId === '' ? null : (siteId as number),
        assetCode: assetCode || null,
        // Everything else is left for the registry to fill. Sending the blank
        // form fields would write empty strings the sync then has to reason
        // about.
        compliance: [],
      })

      const result = await api.syncFromRegistry(created.vehicleId!)
      return { created, result }
    },
    onSuccess: async ({ created, result }) => {
      await invalidateFleet(queryClient)
      setRtoError(null)
      setRtoSaved({
        registration: canonical,
        applied: result.valuesApplied ?? 0,
        identity: result.identityFieldsFilled ?? 0,
        reused: result.reusedExisting ?? false,
        restored: created.wasRestored,
        notes: result.identityNotes ?? [],
      })
    },
    onError: (e: Error) => setRtoError(e.message),
  })

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.createVehicle({
        registration,
        vehicleTypeId: vehicleTypeId as number,
        make: make || null,
        model: model || null,
        chassisNumber: chassisNumber || null,
        registrationYear: registrationYear ? Number(registrationYear) : null,
        assetCode: assetCode || null,
        siteId: siteId === '' ? null : (siteId as number),
        compliance: Object.entries(obligations).map(([typeId, draft]) => ({
          complianceTypeId: Number(typeId),
          status: draft.status as 1 | 2 | 3 | 4,
          expiryDate: draft.status === Status.Known ? draft.expiryDate : null,
        })),
      })

      // Now the vehicle exists, so the scans have something to attach to.
      //
      // Sequentially rather than in parallel: each upload writes an activity entry
      // and may correct a compliance row, and a handful of files is not worth
      // racing. One failure must not abort the rest either — the vehicle is
      // already saved, and the user should be told exactly which scans did not
      // make it rather than losing all of them.
      const failures: AttachOutcome['failures'] = []
      let attached = 0

      for (const [typeId, draft] of Object.entries(obligations)) {
        if (!draft.file) continue

        const name =
          reference.data?.complianceTypes?.find((c) => c.complianceTypeId === Number(typeId))
            ?.displayName ?? `obligation ${typeId}`

        try {
          await api.uploadDocument(created.vehicleId, Number(typeId), draft.file, {
            // The date typed against this obligation is what the certificate says.
            // Not applied to the register: it was just entered by hand there too,
            // so there is nothing to correct.
            documentExpiryDate:
              draft.status === Status.Known && draft.expiryDate ? draft.expiryDate : undefined,
          })
          attached += 1
        } catch (error) {
          failures.push({ obligation: name, message: (error as Error).message })
        }
      }

      return { vehicleId: created.vehicleId, attached, failures }
    },
    onSuccess: async (outcome) => {
      await invalidateFleet(queryClient)

      // A scan that did not attach is worth stopping for. Navigating away with a
      // cheerful redirect would hide it, and the user would believe the
      // certificate is on file when it is not.
      if (outcome.failures.length > 0) {
        setAttachOutcome(outcome)
        return
      }

      // Straight to the new record rather than back to the register: the next
      // thing anyone does after adding a vehicle is check what they just entered.
      navigate(`/vehicles/${canonical}`, { replace: true })
    },
  })

  const duplicate = create.error instanceof ApiError && create.error.isConflict

  // Blocked while a duplicate is showing: the user has a better option on screen,
  // and letting them submit into a guaranteed 409 wastes the round trip.
  const canSubmit =
    canonical.length > 0 && vehicleTypeId !== '' && !create.isPending && !blocked

  function setObligation(typeId: number, patch: Partial<ObligationDraft>) {
    setObligations((current) => ({
      ...current,
      [typeId]: { status: Status.Unknown, expiryDate: '', ...current[typeId], ...patch },
    }))
  }

  return (
    <div className="formpage">
      <header className="formpage__head">
        <button type="button" className="formpage__back" onClick={onClose}>
          ← Register
        </button>
        <div>
          <div className="eyebrow">Register</div>
          <h1>Add a vehicle</h1>
        </div>
      </header>

      <form
        className="formpage__form"
        onSubmit={(e) => {
          e.preventDefault()
          if (canSubmit) create.mutate()
        }}
      >
        <div className="formpage__body">
          <label className="editor__field">
            <span className="eyebrow">Registration number *</span>
            <input
              type="text"
              className="mono add__reg"
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
              placeholder="MH 12 UK 2938"
              autoFocus
              required
            />
            {/* Shows what will actually be stored. "MH 12 UK 2938" and
                "mh-12-uk-2938" are the same vehicle, and seeing that before
                saving prevents the user creating what they think is a new one. */}
            {canonical && (
              <span className="add__canonical mono">stored as {canonical}</span>
            )}
          </label>

          {/* Found before the user fills anything else in. Offers the useful
              action — open the existing record and edit it — instead of just
              refusing the save later. */}
          {blocked && existing.data && (
            <div className="add__existing" role="status">
              <div>
                <b>{existing.data.registration} is already on the register.</b>
                <p>
                  {existing.data.vehicleType ?? 'Unknown type'}
                  {existing.data.site ? ` · ${existing.data.site}` : ' · unassigned'}
                </p>
              </div>
              <button
                type="button"
                className="btn btn--primary"
                // replace, not push. The user came here to add a vehicle, found it
                // already exists, and abandoned the attempt — that dead end must
                // not stay in history. Pushing it left the detail page's
                // "← Register" button walking back into a half-filled add form.
                onClick={() =>
                  navigate(`/vehicles/${existing.data!.registration}`, { replace: true })
                }
              >
                Open &amp; edit it
              </button>
            </div>
          )}

          {/* Removed, not present. Saying so is the whole point: the person is
              about to bring back a record that still carries its documents, its
              history and its activity log, and they should know that rather than
              believing they are starting an empty one. */}
          {removed && existing.data && (
            <div className="add__existing add__existing--gone" role="status">
              <div>
                <b>{existing.data.registration} was removed from the register.</b>
                <p>
                  Saving brings it back with everything it had —{' '}
                  {existing.data.vehicleType ?? 'its type'}
                  {existing.data.site ? `, ${existing.data.site}` : ''}, its documents
                  and its history. What you enter here is applied on top.
                </p>
              </div>
            </div>
          )}

          <div className="syncbar">
            <div>
              <b>Sync from RTO</b>
              <p>
                {!registry.data?.isConfigured
                  ? 'Not available yet — the VAHAN aggregator subscription is still pending. Enter details by hand for now.'
                  : vehicleTypeId === ''
                    ? 'Choose a type first. VAHAN returns a vehicle class that maps onto several RSIIL types, and which one it is decides which obligations apply.'
                    : `Adds the vehicle and fills it from ${registry.data.providerName} — owner, make, model, chassis, engine and every date it holds.`}
              </p>
              {/* Said before the press, not after the bill. */}
              {registry.data?.isConfigured && vehicleTypeId !== '' && (
                <p className="add__cost">
                  One lookup, charged at the provider's per-call rate unless a response
                  for this registration is already held.
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn btn--primary"
              disabled={
                !registry.data?.isConfigured ||
                canonical.length < 6 ||
                vehicleTypeId === '' ||
                blocked ||
                syncNew.isPending
              }
              title={
                registry.data?.isConfigured
                  ? undefined
                  : 'Requires a licensed VAHAN aggregator subscription'
              }
              onClick={() => {
                setRtoError(null)
                syncNew.mutate()
              }}
            >
              {syncNew.isPending ? 'Fetching…' : 'Fetch from RTO & save'}
            </button>
          </div>

          {rtoError && (
            <p className="editor__error" role="alert">
              {rtoError}
            </p>
          )}

          <div className="add__grid">
            <div className="editor__field">
              <label className="eyebrow" htmlFor="add-type">
                Type *
              </label>
              <SearchSelect
                id="add-type"
                options={vehicleTypeOptions(reference.data)}
                value={vehicleTypeId}
                onChange={(v) => setVehicleTypeId(v)}
                placeholder="Search types…"
              />
            </div>

            <div className="editor__field">
              <label className="eyebrow" htmlFor="add-site">
                Site
              </label>
              <SearchSelect
                id="add-site"
                options={siteOptions(reference.data)}
                value={siteId}
                onChange={(v) => setSiteId(v)}
                emptyLabel="Unassigned"
                placeholder="Search sites…"
              />
            </div>

            <label className="editor__field">
              <span className="eyebrow">Make</span>
              <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="TATA" />
            </label>

            <label className="editor__field">
              <span className="eyebrow">Model</span>
              <input value={model} onChange={(e) => setModel(e.target.value)} />
            </label>

            <label className="editor__field">
              <span className="eyebrow">Chassis no.</span>
              <input
                className="mono"
                value={chassisNumber}
                onChange={(e) => setChassisNumber(e.target.value)}
              />
            </label>

            <label className="editor__field">
              <span className="eyebrow">Reg. year</span>
              <input
                type="number"
                min={1950}
                max={2100}
                value={registrationYear}
                onChange={(e) => setRegistrationYear(e.target.value)}
              />
            </label>

            <label className="editor__field">
              <span className="eyebrow">Asset code</span>
              <input
                className="mono"
                value={assetCode}
                onChange={(e) => setAssetCode(e.target.value)}
              />
            </label>
          </div>

          {selectedType?.isMachinery && (
            <p className="editor__conflict">
              <b>{selectedType.name} is classified as machinery.</b> It will be added, but the
              Excel importer excludes this type — check the classification is right.
            </p>
          )}

          <h3 className="report__title">Compliance dates &amp; certificates</h3>
          <p className="add__hint">
            Leave anything you don't know as <b>Unknown</b> — that is a real answer and it
            shows up as something to chase. Don't guess. Attach the scan where you have
            it; it uploads as soon as the vehicle is saved.
          </p>

          <div className="add__obligations">
            {reference.data?.complianceTypes?.map((ct) => {
              const draft = obligations[ct.complianceTypeId!] ?? {
                status: Status.Unknown,
                expiryDate: '',
              }

              return (
                <div key={ct.complianceTypeId} className="add__obligation">
                  <span className="add__obligation-name">{ct.displayName}</span>

                  {/* No emptyLabel: an obligation is always in one of the four
                      states, and "none" would be a fifth that does not exist. */}
                  <SearchSelect
                    options={statusOptions()}
                    value={draft.status}
                    onChange={(status) =>
                      setObligation(ct.complianceTypeId!, { status: Number(status) })
                    }
                    placeholder="State…"
                  />

                  {draft.status === Status.Known && (
                    <input
                      type="date"
                      value={draft.expiryDate}
                      onChange={(e) =>
                        setObligation(ct.complianceTypeId!, { expiryDate: e.target.value })
                      }
                      required
                    />
                  )}

                  {/* Only where the obligation actually takes a certificate — read
                      from acceptsDocument, so Permit and Goods Tax get no control
                      and adding one later is an UPDATE, not a deployment. */}
                  {ct.acceptsDocument && (
                    <label className="add__file">
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={(e) =>
                          setObligation(ct.complianceTypeId!, {
                            file: e.target.files?.[0],
                          })
                        }
                      />
                      <span className="add__file-hint">
                        {draft.file ? draft.file.name : 'PDF / JPEG / PNG, optional'}
                      </span>
                    </label>
                  )}
                </div>
              )
            })}
          </div>

          {/*
            The vehicle saved but at least one scan did not attach. Shown instead of
            redirecting: a cheerful navigation would leave the user believing the
            certificate is on file.
          */}
          {/*
            Saved, and what was saved.

            A modal rather than an inline note: the vehicle now EXISTS, which is a
            state change worth stopping for. Somebody who presses "fetch from RTO"
            expecting a form-fill and gets a saved record without being told would
            go on to press Save and create a duplicate.
          */}
          {rtoSaved && (
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="rto-saved">
              <div className="modal__panel add__saved">
                <h2 id="rto-saved">
                  {rtoSaved.registration} {rtoSaved.restored ? 'restored' : 'saved'}
                </h2>

                <p className="add__savedlead">
                  Fetched from the RTO and written to the register.
                  {rtoSaved.reused && ' A response we already held answered, so nothing was charged.'}
                </p>

                <ul className="add__savedlist">
                  <li>
                    <b>{rtoSaved.applied}</b> obligation{rtoSaved.applied === 1 ? '' : 's'} recorded
                  </li>
                  <li>
                    <b>{rtoSaved.identity}</b> vehicle detail{rtoSaved.identity === 1 ? '' : 's'} filled
                    <span> — make, model, chassis, engine, year</span>
                  </li>
                </ul>

                {/* A kept anchor or a hand-edit. Rare here, since the vehicle is
                    seconds old, but never swallowed. */}
                {rtoSaved.notes.length > 0 && (
                  <ul className="syncnotes">
                    {rtoSaved.notes.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                )}

                <div className="editor__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      // Straight into adding another, which is what somebody
                      // entering a delivery of vehicles is actually doing.
                      setRtoSaved(null)
                      setRegistration('')
                      setAssetCode('')
                    }}
                  >
                    Add another
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => navigate(`/vehicles/${rtoSaved.registration}`)}
                  >
                    Open {rtoSaved.registration}
                  </button>
                </div>
              </div>
            </div>
          )}

          {attachOutcome && (
            <div className="editor__conflict" role="alert">
              <b>
                {canonical} was saved
                {attachOutcome.attached > 0 && ` with ${attachOutcome.attached} certificate${
                  attachOutcome.attached > 1 ? 's' : ''
                } attached`}
                , but {attachOutcome.failures.length} did not upload.
              </b>
              <ul className="add__failures">
                {attachOutcome.failures.map((f) => (
                  <li key={f.obligation}>
                    <b>{f.obligation}:</b> {f.message}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigate(`/vehicles/${canonical}`, { replace: true })}
              >
                Open {canonical} and try again there
              </button>
            </div>
          )}

          {duplicate && (
            <p className="editor__conflict" role="alert">
              {(create.error as Error).message}
            </p>
          )}

          {create.error && !duplicate && (
            <p className="editor__error" role="alert">
              {(create.error as Error).message}
            </p>
          )}

            <p className="editor__note">
              Everything entered here is recorded as hand-entered, so a later spreadsheet
              import or registry refresh will not overwrite it.
            </p>
        </div>

        {/* Sticky rather than pinned to a panel: on a page the actions follow the
            content, and stay reachable without scrolling to the very bottom. */}
        <div className="formpage__foot">
          <div className="editor__actions">
            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
              {create.isPending ? 'Adding…' : 'Add vehicle'}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
